import { rem } from 'polished'
import { useLayoutEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import Heading from 'LuxKit/Typography/Heading'
import HeroImage from 'content/components/HeroImage'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import MerchandiseLandingPageHeroImageOverlay from './MerchandiseLandingPageHeroImageOverlay'
import MerchandiseLandingPageSearchBar from './MerchandiseLandingPageSearchBar'
import useMerchandiseHeroPromos from 'hooks/Merchandise/useMerchandiseHeroPromos'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const SearchContainer = styled(LayoutContainer)`
  order: 1;

  ${mediaQueryUp.tablet} {
    order: 3;
    margin-top: ${rem(-40)};
    z-index: 1;
  }
`

const Content = styled.div`
  order: 3;

  ${mediaQueryUp.tablet} {
    order: 2;
  }
`

const DefaultHeroImage = styled(HeroImage)`
  height: ${rem(180)};
  padding: ${rem(40)} ${rem(20)};

  ${mediaQueryUp.tablet} {
    height: ${rem(240)};
    padding: ${rem(40)} ${rem(20)} ${rem(64)} ${rem(20)};
  }
`

const PromoHero = styled.div`
  height: ${rem(426)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    height: ${rem(394)};
    border-bottom: none;
  }
`

const HeroLoadingSkeleton = styled(LoadingBox)`
  height: ${rem(426)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  ${mediaQueryUp.tablet} {
    height: ${rem(394)};
    border-bottom: none;
  }
`

const defaultImage: App.Image = {
  id: '6yybt90fsf3hprp13x5d',
}

function MerchandiseLandingPageHero() {
  const [heroPromos, fetching] = useMerchandiseHeroPromos()

  // Use device detection for SSR/initial render (from user agent)
  const isMobileDevice = useIsMobileDevice()
  // Use viewport detection for dynamic resizing
  const isMobileViewport = useScreenSizeOnly('mobile')
  // Track if we've mounted to client
  const [hasMounted, setHasMounted] = useState(false)

  // Use useLayoutEffect to run synchronously before paint (reduces flicker)
  useLayoutEffect(() => {
    setHasMounted(true)
  }, [])

  // Use device detection on server/initial, then switch to viewport detection after mount
  // If they match initially, no re-render needed - the value stays the same
  const isMobile = hasMounted ? isMobileViewport : isMobileDevice

  const { images, overlays } = useMemo(() => {
    const images = heroPromos.map(promo => (isMobile && promo.mobileImage) ? promo.mobileImage : promo.image)
    // Map overlays to both desktop and mobile image ids so overlay shows regardless of which is rendered
    const overlays: Record<string, React.ReactNode> = {}
    heroPromos.forEach(promo => {
      const overlay = <MerchandiseLandingPageHeroImageOverlay promo={promo} />
      const keys = [
        promo.image.id ?? promo.image.url ?? '',
        promo.mobileImage?.id ?? promo.mobileImage?.url ?? '',
      ].filter(Boolean)
      keys.forEach(key => {
        overlays[key] = overlay
      })
    })

    return { images, overlays }
  }, [heroPromos, isMobile])

  const hasImages = images.length > 0

  return (
    <Wrapper>
      <SearchContainer relative>
        <MerchandiseLandingPageSearchBar />
      </SearchContainer>

      <Content>
        {hasImages && <PromoHero>
          <ImageCarousel
            images={images}
            imageOverlays={overlays}
            autoScrollSpeed={5000}
            arrowStyle="ghost"
            eagerLoadFirstImage
            imageParams={{
              quality: 'best',
              fit: 'top',
              gravity: 'north',
            }}
          />
        </PromoHero>}
        {!hasImages && fetching && <HeroLoadingSkeleton />}
        {!hasImages && !fetching && <DefaultHeroImage id={defaultImage.id!}>
          <LayoutContainer relative>
            <Heading variant="heading1" align="center" colour="neutral-eight">
              <FormattedMessage
                id="40bPVLjm8UrUcU4P7rlR"
                defaultMessage="Shop with <i>{loyaltyProgramName} Points</i>"
                description="Hero heading for merchandise landing page with loyalty program name"
                values={{
                  loyaltyProgramName: getLoyaltyProgramDisplayName(),
                  i: (chunks) => <i>{chunks}</i>,
                }}
              />
            </Heading>
          </LayoutContainer>
        </DefaultHeroImage>}
      </Content>
    </Wrapper>
  )
}

export default MerchandiseLandingPageHero
